import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { BookOpen, Calendar, User, ArrowRight } from 'lucide-react';

const BlogPage = () => {
  const posts = [
    {
      title: '10 Ways to Reduce Equipment Downtime',
      excerpt: 'Learn proven strategies to minimize downtime and keep your operations running smoothly.',
      category: 'Best Practices',
      author: 'Sarah Johnson',
      date: 'Jan 15, 2026',
      readTime: '5 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'The Future of Maintenance Management',
      excerpt: 'Explore emerging trends and technologies shaping the future of CMMS and maintenance operations.',
      category: 'Industry Trends',
      author: 'Michael Chen',
      date: 'Jan 12, 2026',
      readTime: '8 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'AI Chatbots: Complete Implementation Guide',
      excerpt: 'Step-by-step guide to successfully implementing AI chatbots in your customer support workflow.',
      category: 'Guides',
      author: 'Emily Rodriguez',
      date: 'Jan 10, 2026',
      readTime: '12 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'ROI Calculator: Maintenance Software',
      excerpt: 'Calculate the potential ROI of implementing a CMMS solution in your organization.',
      category: 'Tools',
      author: 'David Kim',
      date: 'Jan 8, 2026',
      readTime: '6 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'Digital Transformation Success Stories',
      excerpt: 'Real examples of companies that successfully transformed their operations with technology.',
      category: 'Case Studies',
      author: 'Sarah Johnson',
      date: 'Jan 5, 2026',
      readTime: '10 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'Security Best Practices for SaaS',
      excerpt: 'Essential security practices every SaaS user should know to protect their data.',
      category: 'Security',
      author: 'Michael Chen',
      date: 'Jan 3, 2026',
      readTime: '7 min read',
      image: '/api/placeholder/400/250',
    },
  ];

  const categories = ['All', 'Best Practices', 'Industry Trends', 'Guides', 'Case Studies', 'Security'];

  return (
    <>
      <Helmet>
        <title>Blog - Insights & Resources | mwbTechs</title>
        <meta name="description" content="Read the latest insights, guides, and best practices for maintenance management and digital transformation." />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-6">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">Blog</h1>
              <p className="text-xl text-gray-600">Insights, guides, and best practices for modern businesses</p>
            </motion.div>
          </div>
        </section>

        <section className="section">
          <div className="container-custom">
            {/* Categories */}
            <div className="flex flex-wrap gap-4 justify-center mb-12">
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    index === 0
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 border border-gray-300 hover:border-primary-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Blog Posts Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.article
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-video bg-gradient-to-br from-primary-100 to-blue-100"></div>
                  <div className="p-6">
                    <span className="inline-block bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium mb-3">
                      {post.category}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-primary-600 transition-colors cursor-pointer">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{post.date}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{post.readTime}</span>
                      <button className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-2">
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            {/* Load More */}
            <div className="text-center mt-12">
              <button className="btn btn-outline text-lg">
                Load More Posts
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="section-sm bg-gradient-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">Subscribe to Our Newsletter</h2>
            <p className="text-xl text-blue-100 mb-8">Get the latest posts delivered directly to your inbox</p>
            <div className="max-w-md mx-auto flex gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-lg text-gray-900"
              />
              <button className="btn btn-white">Subscribe</button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPage;
